import React, { useState, useRef, useEffect } from 'react';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ThemeToggle } from './ThemeToggle';
import { menuItems } from '../data/menuItems';
import { Logo } from './Logo';
import { BlogNavigation } from './BlogNavigation';
import { NewsletterModal } from './NewsletterModal';
import type { MenuItem, Language } from '../types';

const NavItem: React.FC<{ item: MenuItem }> = ({ item }) => {
  const { t } = useLanguage();

  if (item.external) {
    if (item.href && item.href.includes('/blog')) {
      return (
        <BlogNavigation blogUrl={item.href}>
          <span className="text-muted-foreground hover:text-foreground transition-colors text-sm">
            {t('nav', item.label.toLowerCase())}
          </span>
        </BlogNavigation>
      );
    }
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-muted-foreground hover:text-foreground transition-colors text-sm"
      >
        {t('nav', item.label.toLowerCase())}
      </a>
    );
  }

  return (
    <Link
      to={item.href || '/'}
      className="text-muted-foreground hover:text-foreground transition-colors text-sm"
    >
      {t('nav', item.label.toLowerCase())}
    </Link>
  );
};

const LanguageSwitcher: React.FC = () => {
  const { language, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages: { code: Language; name: string; flag: string }[] = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'pl', name: 'Polski', flag: '🇵🇱' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLanguage = languages.find(lang => lang.code === language);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors text-sm"
      >
        <Globe className="w-4 h-4" />
        <span>{currentLanguage?.flag}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-36 bg-background rounded-lg shadow-lg py-1 border border-border">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                changeLanguage(lang.code);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-muted transition-colors text-sm ${
                language === lang.code ? 'text-foreground' : 'text-muted-foreground'
              }`}
            >
              <span>{lang.flag}</span>
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLanguage();
  const [showNewsletter, setShowNewsletter] = useState(false);
  const newsletterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (newsletterRef.current && !newsletterRef.current.contains(event.target as Node)) {
        setShowNewsletter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubscribe = () => {
    setShowNewsletter(true);
  };

  return (
    <nav className="fixed w-full bg-background/80 backdrop-blur-sm z-50 border-b border-border">
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <Logo size="lg" />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            {menuItems.map((item, index) => (
              <NavItem key={index} item={item} />
            ))}
          </div>

          {/* Right Section */}
          <div className="hidden md:flex items-center gap-4">
            <LanguageSwitcher />
            <ThemeToggle />
            <button
              onClick={handleSubscribe}
              className="bg-foreground text-background px-4 py-2 rounded-md text-sm font-medium hover:opacity-90 transition-opacity"
            >
              {t('nav', 'getUpdates')}
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-3">
            <LanguageSwitcher />
            <ThemeToggle />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-foreground"
            >
              {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-background border-t border-border">
          <div className="px-6 py-4 space-y-3">
            {menuItems.map((item, index) => (
              <div key={index}>
                <Link
                  to={item.href || '/'}
                  onClick={() => setIsOpen(false)}
                  className="block text-muted-foreground hover:text-foreground transition-colors text-sm py-2"
                >
                  {t('nav', item.label.toLowerCase())}
                </Link>
              </div>
            ))}
            <button
              onClick={() => {
                handleSubscribe();
                setIsOpen(false);
              }}
              className="w-full bg-foreground text-background px-4 py-2.5 rounded-md text-sm font-medium mt-4"
            >
              {t('nav', 'getUpdates')}
            </button>
          </div>
        </div>
      )}

      <NewsletterModal
        open={showNewsletter}
        title={t('newsletter', 'modalTitle')}
        subtitle={t('newsletter', 'modalSubtitle')}
        placeholder={t('newsletter', 'placeholder')}
        joinLabel={t('newsletter', 'joinButton')}
        maybeLaterLabel={t('nav', 'maybeLater')}
        trustNoSpam={t('newsletter', 'trustNoSpam')}
        trustUnsubscribe={t('newsletter', 'trustUnsubscribe')}
        trustPrivacy={t('newsletter', 'trustPrivacy')}
        onClose={() => setShowNewsletter(false)}
      />
    </nav>
  );
};

